// web/src/services/firestore/docsService.js
import { app, db } from '@/firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit as fsLimit,
} from 'firebase/firestore'

/* ---------------- utilities ---------------- */
const up = (s) => (s ? String(s).toUpperCase() : '')
const normStr = (s) => String(s || '').trim()

function normTags(v) {
  if (Array.isArray(v)) return v.map(normStr).filter(Boolean)
  if (typeof v === 'string')
    return v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  return []
}

function normFile(file = {}) {
  return {
    name: file.name || '',
    size: Number(file.size || 0),
    type: file.type || '',
    downloadURL: file.downloadURL || '',
    path: file.path || '',
  }
}

/* ---------------- mapping helpers ---------------- */
function mapDoc(d) {
  const data = d.data() || {}
  return {
    id: d.id,
    title: data.title || '',
    description: data.description || '', // legacy
    notes: data.notes || '',
    tags: Array.isArray(data.tags) ? data.tags : [],
    communityCode: data.communityCode || '', // expected UPPERCASE
    communityName: data.communityName || data.community || '', // display name
    community: data.community || data.communityName || '', // compatibility
    file: data.file || null, // { name, size, type, downloadURL, path }
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    lastDistributionChange: data.lastDistributionChange || null,
    archivedAt: data.archivedAt ?? null, // null = active
    createdBy: data.createdBy || null, // {email, displayName}

    // Extended fields used by CommunityDocs UI
    type: data.type || '', // human-readable doc type/category
    audience: data.audience || 'community',
    communityCodes: data.communityCodes || [data.communityCode].filter(Boolean),
    communityNames: data.communityNames || [data.communityName].filter(Boolean),
    source: 'DOC',
    sourceRef: { kind: 'doc', id: d.id },
    from: 'Community', // appears in the “From” column
  }
}

function tsMs(ts) {
  if (!ts) return 0
  if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000
  const d = new Date(ts)
  return isNaN(d) ? 0 : d.getTime()
}

/* ---------------- base DOCS (standalone) CRUD ---------------- */

/**
 * List docs (active by default).
 *   listDocsFS()                       -> all communities, active only
 *   listDocsFS('1213WA')               -> one community, active only
 *   listDocsFS(null, true)             -> all communities, include archived
 *   listDocsFS('1213WA', true)         -> one community, include archived
 */
export async function listDocsFS(communityCode = null, includeArchived = false, lim = 500) {
  const base = collection(db, 'docs')
  const clauses = []

  if (communityCode && communityCode !== 'All') {
    clauses.push(where('communityCode', '==', up(communityCode)))
  }
  if (!includeArchived) {
    clauses.push(where('archivedAt', '==', null))
  }

  // createdAt desc; if some legacy rows miss createdAt you can remove this orderBy.
  clauses.push(orderBy('createdAt', 'desc'))
  clauses.push(fsLimit(lim))

  const qy = query(base, ...clauses)
  const snap = await getDocs(qy)
  return snap.docs.map(mapDoc)
}

/** Create a doc record (ensures fields needed for rules & UI). */
export async function addDocFS(payload) {
  const now = serverTimestamp()

  const title = normStr(payload.title)
  const type = normStr(payload.type)
  const communityCode = up(payload.communityCode || payload.community) // accept either
  const communityName = normStr(payload.communityName || payload.community) // display name
  const community = communityName // keep duplicate for rules that check 'community'
  const tags = normTags(payload.tags)
  const notes = normStr(payload.notes)
  const file = normFile(payload.file || {})

  if (!title) throw new Error('Missing title')
  if (!communityCode) throw new Error('Missing community code')
  if (!file.downloadURL) throw new Error('Missing file URL')

  const createdBy =
    payload.createdBy && (payload.createdBy.email || payload.createdBy.displayName)
      ? {
          email: payload.createdBy.email || '',
          displayName: payload.createdBy.displayName || '',
        }
      : null

  const ref = await addDoc(collection(db, 'docs'), {
    title,
    type,
    audience: payload.audience || 'community',
    communityCodes: payload.communityCodes || [communityCode],
    communityNames: payload.communityNames || [communityName],
    tags,
    notes,
    communityCode,
    communityName,
    community, // <- some rule variants check this field
    file,
    archivedAt: null,
    createdBy,
    createdAt: now,
    updatedAt: now,
  })
  return ref.id
}

/** Update a doc record */
export async function updateDocFS(id, changes) {
  const patch = {}
  if (changes.title !== undefined) patch.title = normStr(changes.title)
  if (changes.type !== undefined) patch.type = normStr(changes.type)
  if (changes.tags !== undefined) patch.tags = normTags(changes.tags)
  if (changes.notes !== undefined) patch.notes = normStr(changes.notes)

  if (changes.communityCode !== undefined) {
    patch.communityCode = up(changes.communityCode)
  }
  if (changes.communityName !== undefined || changes.community !== undefined) {
    const name = normStr(changes.communityName || changes.community)
    patch.communityName = name
    patch.community = name
  }

  if (changes.file !== undefined) patch.file = normFile(changes.file)

  patch.updatedAt = serverTimestamp()
  await updateDoc(doc(db, 'docs', id), patch)
}

/** Soft-delete (archive) */
export async function softDeleteDocFS(id) {
  await updateDoc(doc(db, 'docs', id), {
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

/** Restore a soft-deleted doc */
export async function restoreDocFS(id) {
  await updateDoc(doc(db, 'docs', id), {
    archivedAt: null,
    updatedAt: serverTimestamp(),
  })
}

/** Hard delete (rare) */
export async function deleteDocFS(id) {
  await deleteDoc(doc(db, 'docs', id))
}

/** Read one (optional helper) */
export async function getDocFS(id) {
  const snap = await getDoc(doc(db, 'docs', id))
  return snap.exists() ? mapDoc(snap) : null
}

/* ---------------- UNION HELPERS (CAF + PM mirrors) ---------------- */

/**
 * CAF mirror, mapped to CommunityDocs row shape.
 * Expects CAF docs have:
 *   - communityCode
 *   - number or id
 *   - files: [ { title, notes, tags[], docType?, type?, file:{ name, downloadURL, path }, ts } ]
 *   - proposals: [ { vendorName, price, notes, docType?, file:{ name, downloadURL, path }, ts } ]
 */
async function listCafFilesForCommunityFS(communityCode) {
  if (!communityCode) return []

  // Your CAF collection is singular: 'caf'
  const snap = await getDocs(
    query(collection(db, 'caf'), where('communityCode', '==', up(communityCode)))
  )

  const out = []

  for (const d of snap.docs) {
    const caf = d.data() || {}
    const cafId = d.id
    const cafNumber = caf.number || cafId

    // ---- Files & Images from CAF ----
    const files = Array.isArray(caf.files)
      ? caf.files
      : Array.isArray(caf.documents)
      ? caf.documents
      : Array.isArray(caf.attachments)
      ? caf.attachments
      : Array.isArray(caf.docs)
      ? caf.docs
      : []

    files.forEach((f, i) => {
      const fileMeta = f?.file || {}
      if (!fileMeta.downloadURL) return

      const title =
        (typeof f?.title === 'string' && f.title.trim()) ||
        fileMeta.name ||
        'Untitled document'
      const createdAt = f?.ts || caf.updatedAt || caf.createdAt || null
      const tags = normTags(f?.tags)
      const type =
        normStr(f?.docType) ||
        normStr(f?.type) ||
        'CAF Document'

      out.push({
        id: `caf:${cafId}:files:${i}`,
        title,
        description: f?.notes || '',
        notes: f?.notes || '',
        tags,
        communityCode: caf.communityCode || '',
        communityName: caf.communityName || '', // if present on CAF
        file: fileMeta,
        createdAt,
        updatedAt: createdAt,
        archivedAt: null,

        // Extra fields for the UI
        type,
        source: 'CAF',
        sourceRef: { kind: 'caf', cafId, cafNumber, fileIndex: i, origin: 'files' },
        from: `CAF ${cafNumber}`,
      })
    })

    // ---- Proposals from CAF ----
    const proposals = Array.isArray(caf.proposals) ? caf.proposals : []

    proposals.forEach((p, i) => {
      const fileMeta = p?.file || {}
      if (!fileMeta.downloadURL) return

      const baseTitle =
        (typeof p?.vendorName === 'string' && p.vendorName.trim()) ||
        fileMeta.name ||
        'Proposal'
      const title = `Proposal: ${baseTitle}`

      const createdAt = p?.ts || caf.updatedAt || caf.createdAt || null

      // allow tags or docType to show up in search
      const tags = normTags(p?.tags || p?.docType || 'Proposal')
      const type =
        normStr(p?.docType) ||
        'Proposal'

      out.push({
        id: `caf:${cafId}:proposal:${i}`,
        title,
        description: p?.notes || '',
        notes: p?.notes || '',
        tags,
        communityCode: caf.communityCode || '',
        communityName: caf.communityName || '',
        file: fileMeta,
        createdAt,
        updatedAt: createdAt,
        archivedAt: null,

        // Extra fields for the UI
        type,
        source: 'CAF',
        sourceRef: {
          kind: 'cafProposal',
          cafId,
          cafNumber,
          proposalIndex: i,
        },
        from: `CAF ${cafNumber} (Proposal)`,
      })
    })
  }

  return out
}

/** (Future) PM files (mirror) — placeholder to keep signature stable */
async function listPmFilesForCommunityFS(/* communityCode */) {
  return []
}

/**
 * Union reader used by CommunityDocsDev:
 * - Standalone “docs” collection
 * - Files attached to CAFs (and later PMs)
 *
 * @param {string|null} communityCode
 * @param {object} opts
 *   - includeArchived?: boolean
 *   - q?: string
 *   - type?: string ('All' or specific)
 */
export async function listCommunityDocsUnion(communityCode = null, opts = {}) {
  const includeArchived = opts.includeArchived === true
  const qText = normStr(opts.q || '').toLowerCase()
  const typeFilter = opts.type && opts.type !== 'All' ? String(opts.type) : null

  // 1) base docs
  const docs = await listDocsFS(communityCode, includeArchived)

  // 2) CAF files (only when scoped to a single community for perf)
  const cafDocs = communityCode ? await listCafFilesForCommunityFS(communityCode) : []

  // 3) PM files (future)
  const pmDocs = []

  let all = [...docs, ...cafDocs, ...pmDocs]

  if (typeFilter) {
    all = all.filter(
      (r) => String(r.type || '').toLowerCase() === typeFilter.toLowerCase()
    )
  }

  if (qText) {
    all = all.filter((r) => {
      const hay = [
        r.title,
        r.description,
        r.notes,
        r.type,
        r.communityCode,
        r.communityName,
        r?.from,
        Array.isArray(r?.tags) ? r.tags.join(' ') : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(qText)
    })
  }

  all.sort((a, b) => tsMs(b.createdAt || b.updatedAt) - tsMs(a.createdAt || a.updatedAt))
  return all
}
export async function updateDocumentDistributionFS(payload) {
  return (await httpsCallable(getFunctions(app, 'us-central1'), 'updateDocumentDistribution')(payload)).data
}
